import React, { useEffect } from "react";

interface KeyboardShortcutsProps {
  isPopup?: boolean;
  onClose?: () => void;
}

function Key({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "primary" | "success";
}) {
  const variants = {
    default: "bg-gradient-to-b from-gray-50 to-gray-100 border-gray-200 text-gray-700 shadow-sm",
    primary: "bg-gradient-to-b from-blue-50 to-blue-100 border-blue-200 text-blue-700 shadow-md",
    success: "bg-gradient-to-b from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700 shadow-md",
  };

  return (
    <kbd
      className={`inline-flex items-center justify-center min-w-[2rem] h-8 px-2 text-xs font-semibold border rounded-md transition-all duration-200 hover:scale-105 ${variants[variant]}`}
    >
      {children}
    </kbd>
  );
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({ isPopup = false, onClose }) => {
  useEffect(() => {
    if (isPopup) {
      document.body.classList.add("modal-open");
      return () => {
        document.body.classList.remove("modal-open");
      };
    }
  }, [isPopup]);

  useEffect(() => {
    if (isPopup && onClose) {
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isPopup, onClose]);

  const shortcuts = [
    {
      keys: [<Key key="up">↑</Key>, <Key key="down">↓</Key>],
      desc: "Navigate rows",
      icon: "📋",
    },
    {
      keys: [<Key key="left">←</Key>, <Key key="right">→</Key>],
      desc: "Navigate columns",
      icon: "🔄",
    },
    {
      keys: [
        <Key key="ctrl" variant="primary">
          Ctrl
        </Key>,
        <span key="plus" className="mx-1 text-gray-400">
          +
        </span>,
        <Key key="enter" variant="primary">
          Enter
        </Key>,
      ],
      desc: "Validate & save changes",
      icon: "✓",
    },
    {
      keys: [
        <Key key="ctrl" variant="primary">
          ALT
        </Key>,
        <span key="alt" className="mx-1 text-gray-400">
          +
        </span>,
        <Key key="enter" variant="primary">
          Enter
        </Key>,
      ],
      desc: "Save changes",
      icon: "💾",
    },
    {
      keys: [
        <Key key="esc" variant="success">
          Esc
        </Key>,
      ],
      desc: "Cancel edit",
      icon: "❌",
    },
  ];

  const usefulLinks = [
    {
      name: "DeepL Translator",
      url: "https://www.deepl.com/translator",
      icon: "🌐",
      description: "AI-powered translation service",
    },
  ];

  const content = (
    <div
      className={`bg-white ${
        isPopup ? "rounded-xl shadow-2xl border border-gray-100 p-6 w-[600px]" : "rounded-lg border border-gray-200 p-4"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-sm">⌨️</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Keyboard Shortcuts</h3>
        </div>
        {isPopup && onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      <div className="space-y-3">
        {shortcuts.map((shortcut, i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <span className="text-lg">{shortcut.icon}</span>
              <div className="flex items-center space-x-1">{shortcut.keys}</div>
            </div>
            <span className="text-sm font-medium text-gray-600">{shortcut.desc}</span>
          </div>
        ))}
      </div>

      {/* Useful Links Section */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-md flex items-center justify-center">
            <span className="text-white text-xs">🔗</span>
          </div>
          <h4 className="text-md font-semibold text-gray-800">Useful Links</h4>
        </div>

        <div className="space-y-2">
          <div className="space-y-2">
            {usefulLinks.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="block no-underline">
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{link.icon}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-green-700 hover:text-green-800">{link.name}</span>
                      <span className="text-xs text-green-600">{link.description}</span>
                    </div>
                  </div>
                  <span className="text-xs text-green-600">↗</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {isPopup && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="font-display text-sm text-primary leading-relaxed whitespace-pre-line mb-6">
            💡 <strong>Pro tip:</strong> Select a translation cell and press <Key>Enter</Key> to edit. Submit it with
            <Key>CTRL</Key>+<Key> Enter</Key>. Wait a litlle bit you'll be moved automatically to the next cell, you can
            directly start typingin order to translate your input. If you want to save a translation and to continue its
            translation later, You could still do it with <Key>ALT</Key>+<Key> Enter</Key>
          </p>
        </div>
      )}
    </div>
  );

  if (isPopup) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div onClick={(e) => e.stopPropagation()}> {content}</div>
      </div>
    );
  }

  return content;
};

export default KeyboardShortcuts;
